import React, {useEffect, useMemo, useRef, useState} from 'react';
import styled from 'styled-components';
import {
  usePreleadQuery,
  useGetPreleadProjectsQuery,
  useCurrentUserQuery,
  usePreleadContactsQuery,
  useCreateUserMailActivityMutation,
} from 'api';
import TimelineItem from 'components/TimelineItem';
import usePreleadContext from '../usePreleadContext';
import {Form, Button, Input, Select, Popover} from 'components/antd';
import {Quill} from 'components/Ui';
import {SelectProject} from '../SelectProject';
import _ from 'lodash';
import {useFormik} from 'formik';
import * as Yup from 'yup';
import useMailTemplate from 'hooks/useMailTempate';

export default () => {
  const {preleadId} = usePreleadContext();
  const {templates, templateTitle} = useMailTemplate();
  const [cc, setCc] = useState(false);
  const [bcc, setBcc] = useState(false);
  const [variablePopoverVisible, setVariablePopoverVisible] = useState(false);
  const [preleadProjectId, setPreleadProjectId] = useState<string | null>(null);
  const [projectId, setProjectId] = useState<string | null>(null);
  const editorRef = useRef<any>(null);

  if (!preleadId) return null;

  const {data: {prelead: preleadData = {}} = {}, refetch} = usePreleadQuery({
    variables: {uuid: preleadId},
    skip: !preleadId,
    notifyOnNetworkStatusChange: true,
  });

  const {data: {prelead: {preleadProjects = [], projects = []} = {}} = {}} =
    useGetPreleadProjectsQuery({
      variables: {uuid: preleadId},
      skip: !preleadId,
    });

  const uniqProjects = useMemo(() => _.uniq(projects), [projects]);

  const handleSelectProject = (selectedProjectId: string) => {
    if (!selectedProjectId) return;
    const preleadProject = preleadProjects.find(
      (p) => p.projectId === selectedProjectId,
    );
    if (!preleadProject) return;
    setPreleadProjectId(preleadProject.uuid);
    setProjectId(selectedProjectId);
  };

  useEffect(() => {
    try {
      const params = new URLSearchParams(location.search);
      const pf = params.get('projectFilters');
      if (pf) {
        const parsed: any = JSON.parse(pf);
        const candidate = parsed?.project_id || parsed?.projectId;
        if (candidate) {
          handleSelectProject(String(candidate));
          return;
        }
      }
    } catch (_e) {}

    if (preleadData?.lastProjectId) {
      handleSelectProject(String(preleadData.lastProjectId));
    }
  }, [preleadId, preleadProjects.length, preleadData?.lastProjectId]);

  const {data: {currentUser = {}} = {}} = useCurrentUserQuery();
  const prelead = preleadData;
  const {data: {preleadContacts = []} = {}} = usePreleadContactsQuery({
    variables: {preleadUuid: prelead?.uuid},
    skip: !prelead?.uuid,
  });

  const [createUserMail, {loading: sending}] =
    useCreateUserMailActivityMutation({
      onCompleted: (data) => {
        if (data.createUserMailActivity.invalidMessage) {
          alert(data.createUserMailActivity.invalidMessage);
        } else {
          formik.resetForm();
          setCc(false);
          setBcc(false);
          refetch();
        }
      },
    });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      fromEmail: currentUser?.email,
      fromName: currentUser.email
        ? `${currentUser?.lastName} ${currentUser?.firstName}`
        : '',
      to: [] as string[],
      cc: [] as string[],
      bcc: [] as string[],
      title: '',
      body: '',
      bodyHtml: '',
      files: [] as any[],
    },
    validationSchema: Yup.object().shape({
      fromEmail: Yup.string().email().required(),
      to: Yup.array().ensure().of(Yup.string().email()).min(1),
      title: Yup.string().required(),
      body: Yup.string().required(),
    }),
    onSubmit: (values) =>
      createUserMail({
        variables: {uuid: preleadProjectId as string, attributes: values},
      }),
  });

  const preleadContactsThatHaveEmail = preleadContacts.filter(
    (contact: any) => contact.email,
  );

  const companyMailOptions = useMemo(() => {
    const options: {label: string; value: string}[] = [];
    if (prelead?.mailAddress) {
      options.push({
        label: `(代表メール) ${prelead.mailAddress}`,
        value: prelead.mailAddress,
      });
    }
    if (Array.isArray(prelead?.mailAddresses)) {
      prelead.mailAddresses.forEach((ma: any) => {
        if (ma.mail) {
          options.push({
            label: `(${ma.name}) ${ma.mail}`,
            value: ma.mail,
          });
        }
      });
    }
    return options;
  }, [prelead?.mailAddress, prelead?.mailAddresses]);

  return (
    <Container>
      <SelectProject
        setProjectId={handleSelectProject}
        projects={uniqProjects}
        value={projectId || undefined}
      />
      <Composer>
        <Form onFinish={formik.handleSubmit} className="space-y-0">
          <div className="min-h-11 flex items-center border-b border-c-border px-2">
            <label className="w-14 text-sm">宛先</label>
            <div className="flex-1">
              <Select
                mode="tags"
                bordered={false}
                className="w-full"
                value={formik.values.to}
                onChange={(values: [string]) =>
                  formik.setFieldValue(
                    'to',
                    values.filter((value) =>
                      Yup.string().trim().email().isValidSync(value),
                    ),
                  )
                }
              >
                {companyMailOptions.map((opt) => (
                  <Select.Option key={opt.value} value={opt.value}>
                    {opt.label}
                  </Select.Option>
                ))}
                {preleadContactsThatHaveEmail.map((contact: any) => (
                  <Select.Option key={contact.uuid} value={contact.email}>
                    {contact.lastName} {contact.firstName}
                    {`<${contact.email}>`}
                  </Select.Option>
                ))}
              </Select>
            </div>
            <div className="flex gap-1">
              {!cc && (
                <Button
                  type="link"
                  onClick={() => setCc(true)}
                  className="px-1"
                >
                  Cc
                </Button>
              )}
              {!bcc && (
                <Button
                  type="link"
                  onClick={() => setBcc(true)}
                  className="px-1"
                >
                  Bcc
                </Button>
              )}
            </div>
          </div>

          {cc && (
            <div className="min-h-11 flex items-center border-b border-c-border px-2">
              <label className="w-14 text-sm">Cc</label>
              <div className="flex-1">
                <Select
                  mode="tags"
                  bordered={false}
                  className="w-full"
                  value={formik.values.cc}
                  onChange={(values: [string]) =>
                    formik.setFieldValue(
                      'cc',
                      values.filter((value) =>
                        Yup.string().trim().email().isValidSync(value),
                      ),
                    )
                  }
                >
                  {companyMailOptions.map((opt) => (
                    <Select.Option key={opt.value} value={opt.value}>
                      {opt.label}
                    </Select.Option>
                  ))}
                  {preleadContactsThatHaveEmail.map((contact: any) => (
                    <Select.Option key={contact.uuid} value={contact.email}>
                      {contact.lastName} {contact.firstName}
                      {`<${contact.email}>`}
                    </Select.Option>
                  ))}
                </Select>
              </div>
            </div>
          )}

          {bcc && (
            <div className="min-h-11 flex items-center border-b border-c-border px-2">
              <label className="w-14 text-sm">Bcc</label>
              <div className="flex-1">
                <Select
                  mode="tags"
                  bordered={false}
                  className="w-full"
                  value={formik.values.bcc}
                  onChange={(values: [string]) =>
                    formik.setFieldValue(
                      'bcc',
                      values.filter((value) =>
                        Yup.string().trim().email().isValidSync(value),
                      ),
                    )
                  }
                >
                  {companyMailOptions.map((opt) => (
                    <Select.Option key={opt.value} value={opt.value}>
                      {opt.label}
                    </Select.Option>
                  ))}
                  {preleadContactsThatHaveEmail.map((contact: any) => (
                    <Select.Option key={contact.uuid} value={contact.email}>
                      {contact.lastName} {contact.firstName}
                      {`<${contact.email}>`}
                    </Select.Option>
                  ))}
                </Select>
              </div>
            </div>
          )}

          <div className="min-h-11 flex items-center border-b border-c-border px-2">
            <label className="w-14 text-sm">件名</label>
            <Input
              id="title"
              placeholder="件名を入力"
              bordered={false}
              value={formik.values.title}
              onChange={(e) => formik.setFieldValue('title', e.target.value)}
            />
          </div>

          <div className="h-15 flex items-center justify-between mb-2 mt-2 p-2">
            <label className="text-sm">本文</label>
            <Select
              placeholder="メールテンプレート"
              optionFilterProp="children"
              showSearch
              className="ml-auto mr-2 w-[360px]"
              onSelect={(value: any) => {
                const template = templates.find((t) => t.uuid === value);
                if (!template) return;
                formik.setValues({
                  ...formik.values,
                  title: template.mailTemplate.title,
                });
                if (template.mailTemplate.bodyHtml) {
                  editorRef.current?.editor.root &&
                    (editorRef.current.editor.root.innerHTML =
                      template.mailTemplate.bodyHtml);
                  formik.setFieldValue(
                    'bodyHtml',
                    template.mailTemplate.bodyHtml,
                  );
                } else {
                  editorRef.current?.editor.setText(
                    template.mailTemplate.body || '',
                  );
                  formik.setFieldValue(
                    'body',
                    template.mailTemplate.body || '',
                  );
                }
              }}
            >
              {templates.map((template) => (
                <Select.Option key={template.uuid} value={template.uuid}>
                  {templateTitle(template)}
                </Select.Option>
              ))}
            </Select>
            <div>
              <Popover
                content={
                  <div className="flex flex-col py-5">
                    <div className="flex w-59 h-7">
                      <Button
                        className="p-0 pl-2 w-59 text-left border-none hover:bg-blue-50"
                        onClick={() => {
                          if (editorRef.current?.editor.getSelection()) {
                            editorRef.current?.editor.insertText(
                              editorRef.current?.editor.getSelection().index,
                              '{{company}}',
                            );
                          } else {
                            const title: any = document.getElementById('title');
                            const value =
                              title.value.substr(0, title.selectionStart) +
                              '{{company}}' +
                              title.value.substr(title.selectionStart);
                            formik.setFieldValue('title', value);
                          }
                        }}
                      >
                        <span className="text-foreground-light mr-2">
                          {'{{company}}'}
                        </span>
                        <span className="text-foreground">会社</span>
                      </Button>
                    </div>
                  </div>
                }
                trigger="click"
                visible={variablePopoverVisible}
                placement="topRight"
                onVisibleChange={setVariablePopoverVisible}
              >
                <Button className="w-7 flex items-center justify-center ml-2 text-foreground-light">{`{ }`}</Button>
              </Popover>
            </div>
          </div>
          <Quill
            ref={editorRef}
            theme="snow"
            value={formik.values.bodyHtml}
            onChange={(content, _delta, _source, editor) => {
              formik.setFieldValue('bodyHtml', content);
              formik.setFieldValue('body', editor.getText());
            }}
            modules={{
              toolbar: [
                ['bold', 'italic', 'underline'],
                ['code-block'],
                ['link'],
              ],
            }}
            formats={[
              'header',
              'list',
              'bold',
              'italic',
              'underline',
              'strike',
              'color',
              'background',
              'code-block',
              'link',
            ]}
            bounds={'#quill-container'}
            className="min-h-48"
          />
          <div className="p-2 flex items-center gap-2">
            <Button
              type="primary"
              htmlType="submit"
              disabled={
                !formik.isValid ||
                prelead.company?.isUnsubscribed ||
                !preleadProjectId
              }
              loading={sending}
              className="ml-auto"
            >
              送信
            </Button>
          </div>
        </Form>
      </Composer>

      {preleadData?.mailActivities?.map((activity) => (
        <TimelineItem key={activity.uuid} activity={activity} />
      ))}
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 20px;
`;

const Composer = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid var(--border-color);
  box-sizing: border-box;
  border-radius: 4px;
  position: relative;

  label {
    color: var(--text-color-2);
    font-weight: 500;
    font-size: 12px;
  }

  .ql-toolbar {
    display: none;
    border: none;
    position: absolute;
    bottom: 32px;
    transform: translateY(100%);
  }
  .ql-snow {
    border: none;
    padding: 0;
  }
  .ql-editor {
    padding: 8px;
  }
  .ql-toolbar.ql-snow .ql-formats {
    margin-right: 10px;
  }
`;
